import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Bike, Car, Download, RefreshCw, Save, XCircle, Plus, Trash2, Info, Pencil } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

export const CityAdminVehicleSettings = ({ cityInfo }) => {
  const { user } = useAuth();
  const [pricingData, setPricingData] = useState(null);
  const [pricingOverrides, setPricingOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingPricing, setSavingPricing] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const [showAddSubtype, setShowAddSubtype] = useState(null);
  const [newSubtype, setNewSubtype] = useState({ name: "", description: "", fare_multiplier: 1.0 });
  const [editSubtype, setEditSubtype] = useState(null);

  useEffect(() => {
    if (cityInfo?.city?.id) {
      fetchPricingData();
    }
  }, [cityInfo]);

  const fetchPricingData = async () => {
    if (!cityInfo?.city?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/city/${cityInfo.city.id}/pricing`);
      setPricingData(res.data);
      
      // Initialize overrides from existing city pricing
      const overridesMap = {};
      res.data.pricing_overrides?.forEach(po => {
        overridesMap[po.vehicle_type] = {
          ...po.overrides,
          id: po.id
        };
      });
      setPricingOverrides(overridesMap);
    } catch (err) {
      console.error("Failed to load pricing:", err);
      toast.error("Failed to load pricing settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVehicleSettings = async () => {
    if (!editVehicle || !cityInfo?.city?.id) return;
    setSavingPricing(true);
    
    try {
      await axios.post(`${API}/city/${cityInfo.city.id}/pricing`, {
        vehicle_type: editVehicle.vehicle_type,
        // Display name
        display_name: editVehicle.display_name,
        // Booking type toggles
        one_way_enabled: editVehicle.one_way_enabled,
        two_way_enabled: editVehicle.two_way_enabled,
        hourly_enabled: editVehicle.hourly_enabled,
        daily_enabled: editVehicle.daily_enabled,
        package_enabled: editVehicle.package_enabled,
        // Basic pricing
        base_fare: editVehicle.base_fare,
        per_km_fare: editVehicle.per_km_fare,
        per_min_fare: editVehicle.per_min_fare,
        minimum_fare: editVehicle.minimum_fare,
        // Distance limits
        max_pickup_distance_km: editVehicle.max_pickup_distance_km,
        max_ride_distance_km: editVehicle.max_ride_distance_km,
        // Two-way
        waiting_charge_per_minute: editVehicle.waiting_charge_per_minute,
        // Hourly pricing
        hourly_rate: editVehicle.hourly_rate,
        hourly_kms_cap: editVehicle.hourly_kms_cap,
        hourly_extra_km_charge: editVehicle.hourly_extra_km_charge,
        hourly_min_hours: editVehicle.hourly_min_hours,
        hourly_max_hours: editVehicle.hourly_max_hours,
        // Daily pricing
        daily_rate: editVehicle.daily_rate,
        daily_max_kms: editVehicle.daily_max_kms,
        daily_extra_km_charge: editVehicle.daily_extra_km_charge,
        daily_min_days: editVehicle.daily_min_days,
        daily_max_days: editVehicle.daily_max_days,
        // Package pricing
        package_base_fare: editVehicle.package_base_fare,
        package_per_km_rate: editVehicle.package_per_km_rate,
        package_max_weight_kg: editVehicle.package_max_weight_kg,
        package_extra_weight_charge: editVehicle.package_extra_weight_charge
      });
      toast.success(`${editVehicle.display_name || editVehicle.vehicle_type} settings saved`);
      setEditVehicle(null);
      fetchPricingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingPricing(false);
    }
  };

  const handleResetPricing = async (vehicleType) => {
    if (!cityInfo?.city?.id) return;
    if (!window.confirm(`Reset ${vehicleType} pricing to Admin/Global defaults? Your city overrides will be removed.`)) return;
    
    try {
      await axios.post(`${API}/city/${cityInfo.city.id}/pricing/reset/${vehicleType}`);
      toast.success(`Pricing reset to defaults for ${vehicleType}`);
      fetchPricingData();
    } catch (err) {
      toast.error("Failed to reset pricing");
    }
  };

  const handleInheritFromAdmin = async () => {
    if (!window.confirm("This will copy all pricing settings from your Admin. Your current city settings will be overwritten. Continue?")) {
      return;
    }
    try {
      await axios.post(`${API}/city-admin/inherit-admin-pricing?city_admin_id=${user.id}&admin_id=${user.admin_id || cityInfo?.city?.admin_id}`);
      toast.success("Successfully inherited pricing from Admin");
      fetchPricingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to inherit pricing");
    }
  };

  // ========== Subtype Management ==========
  
  const handleAddSubtype = async (vehicleType) => {
    if (!cityInfo?.city?.id || !newSubtype.name.trim()) {
      toast.error("Please enter a subtype name");
      return;
    }
    
    try {
      await axios.post(`${API}/city/${cityInfo.city.id}/subtypes/${vehicleType}`, {
        name: newSubtype.name.trim(),
        description: newSubtype.description || "",
        fare_multiplier: parseFloat(newSubtype.fare_multiplier) || 1.0,
        is_active: true
      });
      toast.success(`Subtype "${newSubtype.name}" added`);
      setShowAddSubtype(null);
      setNewSubtype({ name: "", description: "", fare_multiplier: 1.0 });
      fetchPricingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add subtype");
    }
  };

  const handleUpdateSubtype = async () => {
    if (!cityInfo?.city?.id || !editSubtype) return;
    
    try {
      if (editSubtype.source === "admin") {
        // Create override for admin subtype
        await axios.post(`${API}/city/${cityInfo.city.id}/subtypes/${editSubtype.vehicle_type}/override/${editSubtype.name}`, {
          description: editSubtype.description,
          fare_multiplier: parseFloat(editSubtype.fare_multiplier) || 1.0,
          is_active: editSubtype.is_active
        });
        toast.success(`Override created for "${editSubtype.name}"`);
      } else {
        // Update city subtype
        await axios.put(`${API}/city/${cityInfo.city.id}/subtypes/${editSubtype.vehicle_type}/${editSubtype.id}`, {
          name: editSubtype.name,
          description: editSubtype.description,
          fare_multiplier: parseFloat(editSubtype.fare_multiplier) || 1.0,
          is_active: editSubtype.is_active
        });
        toast.success(`Subtype "${editSubtype.name}" updated`);
      }
      setEditSubtype(null);
      fetchPricingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update subtype");
    }
  };

  const handleDeleteSubtype = async (vehicleType, subtype) => {
    if (!cityInfo?.city?.id) return;
    
    if (subtype.source === "admin") {
      toast.error("Cannot delete Admin subtypes. You can only disable them.");
      return;
    }
    
    if (subtype.source === "city_override") {
      // Remove override
      if (!window.confirm(`Remove your override for "${subtype.name}"? It will revert to Admin values.`)) return;
      try {
        await axios.delete(`${API}/city/${cityInfo.city.id}/subtypes/${vehicleType}/override/${subtype.name}`);
        toast.success(`Override removed for "${subtype.name}"`);
        fetchPricingData();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to remove override");
      }
    } else {
      // Delete city-only subtype
      if (!window.confirm(`Delete subtype "${subtype.name}"? This cannot be undone.`)) return;
      try {
        await axios.delete(`${API}/city/${cityInfo.city.id}/subtypes/${vehicleType}/${subtype.id}`);
        toast.success(`Subtype "${subtype.name}" deleted`);
        fetchPricingData();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to delete subtype");
      }
    }
  };

  const handleToggleSubtype = async (vehicleType, subtype) => {
    if (!cityInfo?.city?.id) return;
    
    const newStatus = !subtype.is_active;
    
    try {
      if (subtype.source === "admin") {
        // Create override to toggle admin subtype
        await axios.post(`${API}/city/${cityInfo.city.id}/subtypes/${vehicleType}/override/${subtype.name}`, {
          is_active: newStatus
        });
      } else {
        // Update city subtype
        await axios.put(`${API}/city/${cityInfo.city.id}/subtypes/${vehicleType}/${subtype.id}`, {
          is_active: newStatus
        });
      }
      toast.success(`${subtype.name} ${newStatus ? "enabled" : "disabled"}`);
      fetchPricingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle subtype");
    }
  };

  // Copy all subtypes from Admin for a specific vehicle type
  const handleCopySubtypesFromAdmin = async (vehicleType) => {
    if (!cityInfo?.city?.id) return;
    
    try {
      const res = await axios.post(`${API}/city/${cityInfo.city.id}/subtypes/${vehicleType}/copy-from-admin`);
      const copied = res.data?.copied || 0;
      if (copied > 0) {
        toast.success(`Copied ${copied} subtypes from Admin for ${vehicleType}`);
        fetchPricingData();
      } else {
        toast.info("No new subtypes to copy from Admin");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to copy subtypes");
    }
  };

  // Copy all subtypes from Admin for all vehicle types
  const handleCopyAllSubtypesFromAdmin = async () => {
    if (!cityInfo?.city?.id) return;
    
    if (!window.confirm("This will copy all Admin subtypes to your city. Existing city subtypes won't be affected. Continue?")) {
      return;
    }
    
    try {
      const res = await axios.post(`${API}/city/${cityInfo.city.id}/subtypes/copy-all-from-admin`);
      const totalCopied = res.data?.total_copied || 0;
      if (totalCopied > 0) {
        toast.success(`Copied ${totalCopied} subtypes from Admin`);
        fetchPricingData();
      } else {
        toast.info("No new subtypes to copy from Admin");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to copy subtypes");
    }
  };

  // Open edit dialog with merged data (global values + city overrides)
  const openEditDialog = (vehicleType) => {
    const globalSettings = pricingData?.global_vehicle_settings?.find(vs => vs.vehicle_type === vehicleType) || {};
    const existingOverride = pricingData?.pricing_overrides?.find(po => po.vehicle_type === vehicleType);
    const cityOverrides = existingOverride?.overrides || pricingOverrides[vehicleType] || {};
    
    // Merge: Global values as base, override with city-specific values
    const mergedSettings = {
      vehicle_type: vehicleType,
      display_name: cityOverrides.display_name || globalSettings.display_name || vehicleType.charAt(0).toUpperCase() + vehicleType.slice(1),
      is_active: globalSettings.is_active !== false,
      // Booking types
      one_way_enabled: cityOverrides.one_way_enabled ?? globalSettings.one_way_enabled ?? true,
      two_way_enabled: cityOverrides.two_way_enabled ?? globalSettings.two_way_enabled ?? true,
      hourly_enabled: cityOverrides.hourly_enabled ?? globalSettings.hourly_enabled ?? true,
      daily_enabled: cityOverrides.daily_enabled ?? globalSettings.daily_enabled ?? true,
      package_enabled: cityOverrides.package_enabled ?? globalSettings.package_enabled ?? true,
      // Basic pricing
      base_fare: cityOverrides.base_fare ?? globalSettings.base_fare ?? 20,
      per_km_fare: cityOverrides.per_km_fare ?? globalSettings.per_km_fare ?? 10,
      per_min_fare: cityOverrides.per_min_fare ?? globalSettings.per_min_fare ?? 1,
      minimum_fare: cityOverrides.minimum_fare ?? globalSettings.minimum_fare ?? 25,
      // Distance limits
      max_pickup_distance_km: cityOverrides.max_pickup_distance_km ?? globalSettings.max_pickup_distance_km ?? 10,
      max_ride_distance_km: cityOverrides.max_ride_distance_km ?? globalSettings.max_ride_distance_km ?? 50,
      // Two-way
      waiting_charge_per_minute: cityOverrides.waiting_charge_per_minute ?? globalSettings.waiting_charge_per_minute ?? 2,
      // Hourly
      hourly_rate: cityOverrides.hourly_rate ?? globalSettings.hourly_rate ?? 100,
      hourly_kms_cap: cityOverrides.hourly_kms_cap ?? globalSettings.hourly_kms_cap ?? 10,
      hourly_extra_km_charge: cityOverrides.hourly_extra_km_charge ?? globalSettings.hourly_extra_km_charge ?? 12,
      hourly_min_hours: cityOverrides.hourly_min_hours ?? globalSettings.hourly_min_hours ?? 1,
      hourly_max_hours: cityOverrides.hourly_max_hours ?? globalSettings.hourly_max_hours ?? 12,
      // Daily
      daily_rate: cityOverrides.daily_rate ?? globalSettings.daily_rate ?? 1500,
      daily_max_kms: cityOverrides.daily_max_kms ?? globalSettings.daily_max_kms ?? 150,
      daily_extra_km_charge: cityOverrides.daily_extra_km_charge ?? globalSettings.daily_extra_km_charge ?? 10,
      daily_min_days: cityOverrides.daily_min_days ?? globalSettings.daily_min_days ?? 1,
      daily_max_days: cityOverrides.daily_max_days ?? globalSettings.daily_max_days ?? 30,
      // Package
      package_base_fare: cityOverrides.package_base_fare ?? globalSettings.package_base_fare ?? 30,
      package_per_km_rate: cityOverrides.package_per_km_rate ?? globalSettings.package_per_km_rate ?? 8,
      package_max_weight_kg: cityOverrides.package_max_weight_kg ?? globalSettings.package_max_weight_kg ?? 10,
      package_extra_weight_charge: cityOverrides.package_extra_weight_charge ?? globalSettings.package_extra_weight_charge ?? 5,
      // Subtypes from global
      subtypes: globalSettings.subtypes || [],
      // Whether we have city overrides
      has_city_override: !!existingOverride?.id
    };
    
    setEditVehicle(mergedSettings);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const vehicleIcons = { bike: Bike, auto: Car, car: Car };
  const vehicleColors = { bike: "bg-blue-500", auto: "bg-green-500", car: "bg-purple-500" };

  // Use global_vehicle_settings if available, otherwise fall back to global_vehicle_types
  const vehicleTypes = pricingData?.global_vehicle_settings || 
    pricingData?.global_vehicle_types?.map(vt => ({ vehicle_type: vt })) || [];

  return (
    <div data-testid="city-admin-vehicle-settings">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold">Vehicle Settings</h2>
          <p className="text-muted-foreground">
            Configure vehicle pricing for {cityInfo?.city?.name || "your city"}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={handleCopyAllSubtypesFromAdmin} className="gap-2">
            <Plus className="w-4 h-4" />
            Copy Subtypes from Admin
          </Button>
          <Button variant="outline" onClick={handleInheritFromAdmin} className="gap-2">
            <Download className="w-4 h-4" />
            Inherit Pricing
          </Button>
          <Button variant="outline" onClick={fetchPricingData}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Pricing Hierarchy Info */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-6 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div>
          <h4 className="font-semibold text-blue-800 mb-1">Pricing Hierarchy</h4>
          <div className="text-sm text-blue-700 space-y-0.5">
            <p>1. <span className="font-medium">Super Admin Global Rates</span> - Platform defaults</p>
            <p>2. <span className="font-medium">Admin Rates</span> - Your admin's network rates</p>
            <p>3. <span className="font-medium text-blue-900">City Rates (You)</span> - City-specific overrides</p>
          </div>
          <p className="text-xs text-blue-600 mt-2">
            Your settings override Admin rates. Inherited values shown as defaults.
          </p>
        </div>
      </div>

      {vehicleTypes.length > 0 ? (
        <div className="grid gap-6">
          {vehicleTypes.map((vs) => {
            const vehicleType = vs.vehicle_type;
            const Icon = vehicleIcons[vehicleType] || Car;
            const existingOverride = pricingData?.pricing_overrides?.find(po => po.vehicle_type === vehicleType);
            const globalValues = vs;
            const cityOverrides = existingOverride?.overrides || pricingOverrides[vehicleType] || {};

            return (
              <div key={vehicleType} className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 ${vehicleColors[vehicleType] || 'bg-gray-500'} rounded-lg flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold capitalize">
                        {cityOverrides.display_name || globalValues.display_name || vehicleType}
                      </h3>
                      <div className="flex items-center gap-2">
                        {existingOverride?.id ? (
                          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
                            Custom City Rates
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                            Inherited from Admin
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          • {globalValues.subtypes?.length || 0} subtypes
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEditDialog(vehicleType)}>
                      Edit Settings
                    </Button>
                    {existingOverride?.id && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-destructive border-destructive hover:bg-destructive/10"
                        onClick={() => handleResetPricing(vehicleType)}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reset
                      </Button>
                    )}
                  </div>
                </div>

                {/* Quick Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Base Fare</p>
                    <p className="font-semibold">₹{cityOverrides.base_fare ?? globalValues.base_fare ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Per KM Rate</p>
                    <p className="font-semibold">₹{cityOverrides.per_km_fare ?? globalValues.per_km_fare ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Per Min Rate</p>
                    <p className="font-semibold">₹{cityOverrides.per_min_fare ?? globalValues.per_min_fare ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Min Fare</p>
                    <p className="font-semibold">₹{cityOverrides.minimum_fare ?? globalValues.minimum_fare ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Max Pickup</p>
                    <p className="font-semibold">{cityOverrides.max_pickup_distance_km ?? globalValues.max_pickup_distance_km ?? 10} km</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Max Ride</p>
                    <p className="font-semibold">{cityOverrides.max_ride_distance_km ?? globalValues.max_ride_distance_km ?? 50} km</p>
                  </div>
                </div>

                {/* Subtypes Section - Fully Editable */}
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium">Subtypes</p>
                    <div className="flex gap-2">
                      {globalValues.admin_subtypes && globalValues.admin_subtypes.length > 0 && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="gap-1 text-xs"
                          onClick={() => handleCopySubtypesFromAdmin(vehicleType)}
                        >
                          <Download className="w-3 h-3" /> Copy from Admin
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="gap-1"
                        onClick={() => setShowAddSubtype(vehicleType)}
                      >
                        <Plus className="w-3 h-3" /> Add Subtype
                      </Button>
                    </div>
                  </div>
                  
                  {globalValues.subtypes && globalValues.subtypes.length > 0 ? (
                    <div className="grid gap-2">
                      {globalValues.subtypes.map((subtype) => (
                        <div 
                          key={subtype.id || subtype.name} 
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            subtype.is_active !== false 
                              ? subtype.source === 'city_only' 
                                ? 'bg-green-50 border-green-200' 
                                : subtype.source === 'city_override' 
                                  ? 'bg-blue-50 border-blue-200'
                                  : 'bg-muted border-transparent'
                              : 'bg-muted/50 opacity-60 border-transparent'
                          }`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">{subtype.name}</span>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                subtype.fare_multiplier === 1 
                                  ? 'bg-gray-200 text-gray-700' 
                                  : subtype.fare_multiplier < 1 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-blue-100 text-blue-700'
                              }`}>
                                {subtype.fare_multiplier || 1}x fare
                              </span>
                              {subtype.source === 'admin' && (
                                <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">Admin</span>
                              )}
                              {subtype.source === 'city_override' && (
                                <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                                  Override (Admin: {subtype.admin_fare_multiplier}x)
                                </span>
                              )}
                              {subtype.source === 'city_only' && (
                                <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">City Only</span>
                              )}
                              {subtype.is_active === false && (
                                <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">Disabled</span>
                              )}
                            </div>
                            {subtype.description && (
                              <p className="text-xs text-muted-foreground mt-1">{subtype.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Switch
                              checked={subtype.is_active !== false}
                              onCheckedChange={() => handleToggleSubtype(vehicleType, subtype)}
                              className="scale-75"
                            />
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-8 w-8 p-0"
                              onClick={() => setEditSubtype({ ...subtype, vehicle_type: vehicleType })}
                            >
                              <Pencil className="w-3 h-3 text-blue-500" />
                            </Button>
                            {(subtype.source === 'city_only' || subtype.source === 'city_override') && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-8 w-8 p-0"
                                onClick={() => handleDeleteSubtype(vehicleType, subtype)}
                              >
                                <Trash2 className="w-3 h-3 text-red-500" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No subtypes configured. Click "Add Subtype" to create city-specific subtypes.
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-card rounded-xl border p-8 text-center">
          <Car className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">No vehicle types configured.</p>
          <p className="text-sm text-muted-foreground mt-1">Contact your Admin to set up vehicle types.</p>
        </div>
      )}

      {/* Edit Vehicle Settings Dialog - Exact copy of Admin version */}
      <Dialog open={!!editVehicle} onOpenChange={() => setEditVehicle(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              Edit {editVehicle?.display_name || editVehicle?.vehicle_type} Settings
              {editVehicle?.has_city_override && (
                <span className="ml-2 text-xs font-normal px-2 py-0.5 bg-green-100 text-green-700 rounded">
                  Custom City Rates
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Display Name</Label>
                <Input 
                  value={editVehicle?.display_name || ""} 
                  onChange={(e) => setEditVehicle({ ...editVehicle, display_name: e.target.value })} 
                  placeholder="e.g., Bike, Auto, Car"
                />
              </div>
            </div>
            
            {/* Booking Types Available */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                Available Booking Types
              </h4>
              <p className="text-sm text-muted-foreground mb-3">Turn ON/OFF different booking types for this vehicle in your city</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <span className="text-sm font-medium">One Way</span>
                  <Switch 
                    checked={editVehicle?.one_way_enabled !== false} 
                    onCheckedChange={(checked) => setEditVehicle({ ...editVehicle, one_way_enabled: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <span className="text-sm font-medium">Two Way</span>
                  <Switch 
                    checked={editVehicle?.two_way_enabled !== false} 
                    onCheckedChange={(checked) => setEditVehicle({ ...editVehicle, two_way_enabled: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <span className="text-sm font-medium">Hourly</span>
                  <Switch 
                    checked={editVehicle?.hourly_enabled !== false} 
                    onCheckedChange={(checked) => setEditVehicle({ ...editVehicle, hourly_enabled: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <span className="text-sm font-medium">Daily</span>
                  <Switch 
                    checked={editVehicle?.daily_enabled !== false} 
                    onCheckedChange={(checked) => setEditVehicle({ ...editVehicle, daily_enabled: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <span className="text-sm font-medium">Package</span>
                  <Switch 
                    checked={editVehicle?.package_enabled !== false} 
                    onCheckedChange={(checked) => setEditVehicle({ ...editVehicle, package_enabled: checked })}
                  />
                </div>
              </div>
            </div>
            
            {/* One-Way / Base Pricing */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                One-Way Pricing (Base)
                {editVehicle?.one_way_enabled === false && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Disabled</span>}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Base Fare (₹)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="1"
                    value={editVehicle?.base_fare || 0} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, base_fare: parseFloat(e.target.value) })} 
                  />
                </div>
                <div>
                  <Label>Per KM Rate (₹)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="0.5"
                    value={editVehicle?.per_km_fare || 0} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, per_km_fare: parseFloat(e.target.value) })} 
                  />
                </div>
                <div>
                  <Label>Per Minute Rate (₹)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="0.5"
                    value={editVehicle?.per_min_fare || 0} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, per_min_fare: parseFloat(e.target.value) })} 
                  />
                </div>
                <div>
                  <Label>Minimum Fare (₹)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="1"
                    value={editVehicle?.minimum_fare || 0} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, minimum_fare: parseFloat(e.target.value) })} 
                  />
                </div>
              </div>
            </div>

            {/* Two-Way Pricing */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                Two-Way / Round Trip Pricing
                {editVehicle?.two_way_enabled === false && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Disabled</span>}
              </h4>
              <p className="text-xs text-muted-foreground mb-3">Uses base pricing for fare + waiting charges per minute</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Waiting Charge (₹/min)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="0.5"
                    value={editVehicle?.waiting_charge_per_minute || 2} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, waiting_charge_per_minute: parseFloat(e.target.value) })} 
                    disabled={editVehicle?.two_way_enabled === false}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Charged while driver waits at destination</p>
                </div>
              </div>
            </div>

            {/* Hourly Booking Pricing */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                Hourly Booking Pricing
                {editVehicle?.hourly_enabled === false && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Disabled</span>}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label>Hourly Rate (₹/hr)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="10"
                    value={editVehicle?.hourly_rate || 100} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, hourly_rate: parseFloat(e.target.value) })} 
                  />
                </div>
                <div>
                  <Label>KMs Cap Per Hour</Label>
                  <Input 
                    type="number"
                    min="1"
                    step="1"
                    value={editVehicle?.hourly_kms_cap || 10} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, hourly_kms_cap: parseFloat(e.target.value) })} 
                  />
                  <p className="text-xs text-muted-foreground mt-1">KMs included per hour</p>
                </div>
                <div>
                  <Label>Extra KM Charge (₹)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="1"
                    value={editVehicle?.hourly_extra_km_charge || 12} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, hourly_extra_km_charge: parseFloat(e.target.value) })} 
                  />
                </div>
                <div>
                  <Label>Min Hours</Label>
                  <Input 
                    type="number"
                    min="1"
                    max="24"
                    step="1"
                    value={editVehicle?.hourly_min_hours || 1} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, hourly_min_hours: parseInt(e.target.value) })} 
                  />
                </div>
                <div>
                  <Label>Max Hours/Day</Label>
                  <Input 
                    type="number"
                    min="1"
                    max="24"
                    step="1"
                    value={editVehicle?.hourly_max_hours || 12} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, hourly_max_hours: parseInt(e.target.value) })} 
                  />
                </div>
              </div>
            </div>

            {/* Daily Booking Pricing */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                Daily Booking Pricing
                {editVehicle?.daily_enabled === false && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Disabled</span>}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label>Daily Rate (₹/day)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="100"
                    value={editVehicle?.daily_rate || 1500} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, daily_rate: parseFloat(e.target.value) })} 
                    disabled={editVehicle?.daily_enabled === false}
                  />
                </div>
                <div>
                  <Label>Max KMs Per Day</Label>
                  <Input 
                    type="number"
                    min="10"
                    step="10"
                    value={editVehicle?.daily_max_kms || 150} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, daily_max_kms: parseFloat(e.target.value) })} 
                    disabled={editVehicle?.daily_enabled === false}
                  />
                  <p className="text-xs text-muted-foreground mt-1">KMs included per day</p>
                </div>
                <div>
                  <Label>Extra KM Charge (₹)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="1"
                    value={editVehicle?.daily_extra_km_charge || 10} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, daily_extra_km_charge: parseFloat(e.target.value) })} 
                    disabled={editVehicle?.daily_enabled === false}
                  />
                </div>
                <div>
                  <Label>Min Days</Label>
                  <Input 
                    type="number"
                    min="1"
                    max="30"
                    step="1"
                    value={editVehicle?.daily_min_days || 1} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, daily_min_days: parseInt(e.target.value) })} 
                    disabled={editVehicle?.daily_enabled === false}
                  />
                </div>
                <div>
                  <Label>Max Days</Label>
                  <Input 
                    type="number"
                    min="1"
                    max="90"
                    step="1"
                    value={editVehicle?.daily_max_days || 30} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, daily_max_days: parseInt(e.target.value) })} 
                    disabled={editVehicle?.daily_enabled === false}
                  />
                </div>
              </div>
            </div>

            {/* Package Delivery Pricing */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-teal-500"></span>
                Package Delivery Pricing
                {editVehicle?.package_enabled === false && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Disabled</span>}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Base Fare (₹)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="5"
                    value={editVehicle?.package_base_fare || 30} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, package_base_fare: parseFloat(e.target.value) })} 
                    disabled={editVehicle?.package_enabled === false}
                  />
                </div>
                <div>
                  <Label>Per KM Rate (₹)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="1"
                    value={editVehicle?.package_per_km_rate || 8} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, package_per_km_rate: parseFloat(e.target.value) })} 
                  />
                </div>
                <div>
                  <Label>Max Weight (kg)</Label>
                  <Input 
                    type="number"
                    min="1"
                    max="100"
                    step="1"
                    value={editVehicle?.package_max_weight_kg || 10} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, package_max_weight_kg: parseFloat(e.target.value) })} 
                  />
                  <p className="text-xs text-muted-foreground mt-1">Weight included in base fare</p>
                </div>
                <div>
                  <Label>Extra Weight (₹/kg)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="1"
                    value={editVehicle?.package_extra_weight_charge || 5} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, package_extra_weight_charge: parseFloat(e.target.value) })} 
                  />
                  <p className="text-xs text-muted-foreground mt-1">Charge per kg over limit</p>
                </div>
              </div>
            </div>
            
            {/* Distance Limits */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Distance Limits</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Max Pickup Distance (km)</Label>
                  <Input 
                    type="number"
                    min="1"
                    max="50"
                    step="1"
                    value={editVehicle?.max_pickup_distance_km || 10} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, max_pickup_distance_km: parseFloat(e.target.value) })} 
                  />
                  <p className="text-xs text-muted-foreground mt-1">How far a driver can be from pickup</p>
                </div>
                <div>
                  <Label>Max Ride Distance (km)</Label>
                  <Input 
                    type="number"
                    min="1"
                    max="500"
                    step="5"
                    value={editVehicle?.max_ride_distance_km || 50} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, max_ride_distance_km: parseFloat(e.target.value) })} 
                  />
                  <p className="text-xs text-muted-foreground mt-1">Max distance customer can travel</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditVehicle(null)}>Cancel</Button>
            <Button onClick={handleSaveVehicleSettings} disabled={savingPricing}>
              <Save className="w-4 h-4 mr-2" />
              {savingPricing ? "Saving..." : "Save Settings"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Subtype Dialog */}
      <Dialog open={!!showAddSubtype} onOpenChange={() => { setShowAddSubtype(null); setNewSubtype({ name: "", description: "", fare_multiplier: 1.0 }); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add City Subtype for {showAddSubtype?.charAt(0).toUpperCase()}{showAddSubtype?.slice(1)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Subtype Name *</Label>
              <Input 
                value={newSubtype.name} 
                onChange={(e) => setNewSubtype({ ...newSubtype, name: e.target.value })} 
                placeholder="e.g., Premium, Economy, Ladies Special"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                value={newSubtype.description} 
                onChange={(e) => setNewSubtype({ ...newSubtype, description: e.target.value })} 
                placeholder="e.g., AC vehicle with extra comfort"
              />
            </div>
            <div>
              <Label>Fare Multiplier</Label>
              <Input 
                type="number"
                min="0.1"
                max="5"
                step="0.1"
                value={newSubtype.fare_multiplier} 
                onChange={(e) => setNewSubtype({ ...newSubtype, fare_multiplier: parseFloat(e.target.value) || 1.0 })} 
              />
              <p className="text-xs text-muted-foreground mt-1">
                1.0 = Same as base fare, 1.5 = 50% extra, 0.8 = 20% discount
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddSubtype(null); setNewSubtype({ name: "", description: "", fare_multiplier: 1.0 }); }}>Cancel</Button>
            <Button onClick={() => handleAddSubtype(showAddSubtype)} disabled={!newSubtype.name.trim()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Subtype
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Subtype Dialog */}
      <Dialog open={!!editSubtype} onOpenChange={() => setEditSubtype(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Edit Subtype: {editSubtype?.name}
              {editSubtype?.source === 'admin' && (
                <span className="ml-2 text-xs font-normal px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                  Creating Override
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {editSubtype?.source !== 'admin' && (
              <div>
                <Label>Subtype Name</Label>
                <Input 
                  value={editSubtype?.name || ""} 
                  onChange={(e) => setEditSubtype({ ...editSubtype, name: e.target.value })} 
                  disabled={editSubtype?.source === 'admin'}
                />
              </div>
            )}
            <div>
              <Label>Description</Label>
              <Input 
                value={editSubtype?.description || ""} 
                onChange={(e) => setEditSubtype({ ...editSubtype, description: e.target.value })} 
              />
            </div>
            <div>
              <Label>Fare Multiplier</Label>
              <Input 
                type="number"
                min="0.1"
                max="5"
                step="0.1"
                value={editSubtype?.fare_multiplier || 1.0} 
                onChange={(e) => setEditSubtype({ ...editSubtype, fare_multiplier: parseFloat(e.target.value) || 1.0 })} 
              />
              {editSubtype?.source === 'admin' && editSubtype?.admin_fare_multiplier && (
                <p className="text-xs text-muted-foreground mt-1">
                  Admin value: {editSubtype.admin_fare_multiplier}x
                </p>
              )}
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div>
                <p className="font-medium">Active Status</p>
                <p className="text-xs text-muted-foreground">Enable or disable this subtype in your city</p>
              </div>
              <Switch
                checked={editSubtype?.is_active !== false}
                onCheckedChange={(checked) => setEditSubtype({ ...editSubtype, is_active: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSubtype(null)}>Cancel</Button>
            <Button onClick={handleUpdateSubtype}>
              <Save className="w-4 h-4 mr-2" />
              {editSubtype?.source === 'admin' ? 'Create Override' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CityAdminVehicleSettings;
